import { ControlValueAccessor } from '@angular/forms';
import { BaseFieldComponent } from './base.component';
import { Directive, Input } from '@angular/core';

/**
 * required @Directive decorator so that angular knows there are other angular decorators within this class
 * https://github.com/angular/angular/issues/35295
 */
@Directive()
export abstract class AbstractControlValueAccessor<T> extends BaseFieldComponent implements ControlValueAccessor {
    /*
     * @deprecated Should only be used in angularjs code when trying to pass an expression to the ng-required attribute
     * Example:
     * <mon-form-field [ng-required]="expression"></mon-form-field>
     */
    @Input() set ngRequired (value: boolean | string) {
        this.isRequired = this.getValue(value);
    }

    @Input() set required (value: boolean | string) {
        this.isRequired = this.getValue(value);
    }

    get required (): boolean {
        return this.isRequired;
    }

    /*
     * @deprecated Should only be used in angularjs code when trying to pass an expression to the ng-disabled attribute
     * Example:
     * <mon-form-field [ng-disabled]="expression"></mon-form-field>
     */
    @Input() set ngDisabled (value: boolean | string) {
        this.isDisabled = this.getValue(value);
    }

    @Input() set disabled (value: boolean | string) {
        this.isDisabled = this.getValue(value);
    }

    get disabled (): boolean {
        return this.isDisabled;
    }

    // eslint-disable-next-line
    // @ts-ignore
    private modelValue: T;
    private isRequired: boolean = false;
    private isDisabled: boolean = false;

    public get value (): T {
        return this.modelValue;
    }

    public set value (v: T) {
        if (v !== this.modelValue) {
            this.modelValue = v;
            this.onChange(v);
        }
    }

    /** *************************************************
     * Implementation of ControlValueAccessor
     */

    writeValue (value: T): void {
        /**
         * https://github.com/angular/angular/issues/29218
         *
         * This will let us avoid a wrong state of dirty/pristine properties in a form.
         * That can be important for validation, but we still can use an empty value if we need it.
         */
        if (value !== undefined && value !== null) {
            this.modelValue = value;
            this.onChange(value);
        }
    }

    /**
     * Registers a callback function that is called when the control's value changes in the UI.
     */
    registerOnChange (fn: (value: any) => void): void {
        this.onChange = fn;
    }

    /**
     * Registers a function called when the control is touched.
     */
    registerOnTouched (fn: () => void): void {
        this.onTouch = fn;
    }

    setDisabledState (disabled: boolean): void {
        this.disabled = disabled;
    }

    /**
     * * The registered callback function called when a blur event occurs on the input element.
     */
    onTouch: () => void = () => {};

    /**
     * The registered callback function called when a change or input event occurs on the input
     */
    onChange: (value: T) => void = () => {};

    // **************************************************

    private getValue (input: boolean | string): boolean {
        return typeof input === 'boolean' ? input : true;
    }
}
